import { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Avatar, Card, Stack, SvgIcon, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { RoundedButton } from '@/components/buttons';
import { Header } from '@/layouts/Header';
import { PageContainer, PageContent } from '@/layouts/Page';
import { useOrdersStoreActions } from '@/pages/orders/useOrdersStore';
import { ReactComponent as AttachmentFileIcon } from '@/shared/assets/icons-2.0/attach_file.svg';
import { ReactComponent as InfoIcon } from '@/shared/assets/icons-2.0/info.svg';
import { OrdersPaths } from '@/shared/lib/react-router';

import { ReconciliationUploadConfirmationDialog } from '../dialogs/ReconciliationUploadConfirmationDialog';
import {
  resetReconciliationFileStore,
  useReconciliationFile,
  useReconciliationFileStoreActions,
  useReconciliationOrderGuid,
  useReconciliationOrdersPeriod,
} from '../reconciliation-file.store';
import { ReconciliationBindDialog } from './dialogs/ReconciliationBindDialog';
import { ReconciliationSkipBindingDialog } from './dialogs/ReconciliationSkipBindingDialog';
import { useResetReconciliationBindingStoreOnUnmount } from './reconciliation-binding.store';
import { ReconciliationBindingContent } from './ReconciliationBindingContent';
import { ReconciliationBindingSummaryBar } from './ReconciliationBindingSummaryBar';

const StyledFileToolbar = styled(Card)(({ theme }) => ({
  width: '100%',
  minHeight: '72px',
  flexShrink: 1,
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(3),
})) as typeof Card;

const FileInfoBar = () => {
  const { t } = useTranslation('orders-page');
  const navigate = useNavigate();
  const reconciliationFile = useReconciliationFile();
  const orderGuid = useReconciliationOrderGuid();
  const ordersPeriod = useReconciliationOrdersPeriod();
  const { openUploadConfirmationDialog } = useReconciliationFileStoreActions();
  const { setPendingReconciliationUploadOrderGuid, setPeriod } = useOrdersStoreActions();

  const handleUploadConfirm = useCallback(() => {
    setPendingReconciliationUploadOrderGuid(orderGuid);

    if (ordersPeriod.length === 2) {
      setPeriod(ordersPeriod);
    }

    resetReconciliationFileStore();
    navigate(OrdersPaths.ROOT);
  }, [orderGuid, ordersPeriod, setPendingReconciliationUploadOrderGuid, setPeriod, navigate]);

  return (
    <Fragment>
      <Stack direction="row" alignItems="center" sx={{ p: 1, pb: 0 }} spacing={1}>
        <Card
          variant="outlined"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, flexShrink: 0 }}
        >
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <SvgIcon component={InfoIcon} inheritViewBox sx={{ color: 'common.white' }} />
          </Avatar>
        </Card>

        <StyledFileToolbar
          component={Stack}
          variant="outlined"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={5}
        >
          <Stack direction="row" flex={1} justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar>
                <SvgIcon>
                  <AttachmentFileIcon />
                </SvgIcon>
              </Avatar>
              <Typography component="div" variant="Headline/Bold/small">
                {reconciliationFile?.name ?? ''}
              </Typography>
            </Stack>
          </Stack>
          <RoundedButton variant="outlined" onClick={openUploadConfirmationDialog}>
            {t('reconciliation.field-matching.upload-file')}
          </RoundedButton>
        </StyledFileToolbar>
      </Stack>
      <ReconciliationUploadConfirmationDialog onConfirm={handleUploadConfirm} />
    </Fragment>
  );
};

const ReconciliationBindingPage = () => {
  useResetReconciliationBindingStoreOnUnmount();
  const { t } = useTranslation('orders-page');

  return (
    <Fragment>
      <PageContainer title={t('reconciliation.binding.page-title')}>
        <Header hasBreadcrumbs />
        <FileInfoBar />
        <PageContent>
          <ReconciliationBindingContent />
        </PageContent>
        <ReconciliationBindingSummaryBar />
      </PageContainer>

      <ReconciliationBindDialog />
      <ReconciliationSkipBindingDialog />
    </Fragment>
  );
};

export default ReconciliationBindingPage;
